import {
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { NgbTabset } from '@cmps/ng-bootstrap';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, switchMap, take, tap } from 'rxjs/operators';

import { CMPS_WIDGET_OPTIONS } from '../widget/widget.metadata';
import { WidgetService } from '../widget/widget.service';

import { TranslationKeyMap } from './legacy-widget.i18n';
import { LegacyWidgetService } from './legacy-widget.service';
import {
    MbnaCompliancePopup,
    MbnaCompliancePopupCategoriesIdValue,
    MbnaCompliancePopupCategory,
    MbnaCompliancePopUpColumn,
    MbnaCompliancePopupUser,
} from './mbna-compliance.widget.model';

@Component({
    selector: 'cmps-widget-mbna-compliance',
    templateUrl: './mbna-compliance.widget.component.html',
})
export class MbnaComplianceWidgetComponent implements OnInit, OnDestroy {
    @ViewChild('popupEl') private readonly _popupEl: ElementRef;
    @ViewChild('tabSet') private readonly _tabSet: NgbTabset;

    @Input() options: any;

    i18n = TranslationKeyMap;
    private _sub: Subscription;
    hasPendingData: boolean;
    data: MbnaCompliancePopup;
    currentDetail: MbnaCompliancePopupUser;
    activeTabId: string;
    selectedDateRange: number;
    usersInDrill: MbnaCompliancePopupUser[];
    columns: MbnaCompliancePopUpColumn[];
    populationType: any;
    drillPath: MbnaCompliancePopupUser[] = [];

    constructor(
        @Optional() @Inject(CMPS_WIDGET_OPTIONS) private readonly _options: any,
        private readonly _widgetService: WidgetService,
        private readonly _service: LegacyWidgetService,
    ) {
        this.options = _options || this.options;
        this.selectedDateRange = this.options.selectedDateRange;
        this.populationType = this.options.populationType;
    }

    ngOnInit(): void {
        this._sub = this._widgetService.dateRangeCompliance
            .pipe(
                tap((dateRange) => {
                    this.data = null;
                    this.currentDetail = null;
                    this.hasPendingData = true;
                    this.selectedDateRange =
                        this.options.dateRanges.indexOf(dateRange);
                }),
                switchMap(() =>
                    this._service
                        .mbnaCompliancePopup(
                            this.options.definitionId,
                            this.populationType,
                        )
                        .pipe(
                            catchError(() => of(null)),
                            tap((data) => {
                                this.data = data;
                                this.drillPath = [];
                                if (data != null) {
                                    this.columns = this.buildColumns(data);
                                    if (this.data.items.length > 0) {
                                        this.drillPath.push(
                                            this.data.items[0].user,
                                        );
                                    }
                                }
                                this.hasPendingData = false;
                            }),
                        ),
                ),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.hasPendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    buildColumns(data: MbnaCompliancePopup) {
        const columns: MbnaCompliancePopUpColumn[] = [];
        data.items.forEach((i) => {
            this.getColumns(columns, i.user.categories);
            if (i.overall) {
                this.getColumns(columns, i.overall.categories);
            }
            const categories = i.team
                .map((t) => t.categories)
                .reduce(
                    (p, c) => [...Object.values(p), ...Object.values(c)],
                    {},
                );
            this.getColumns(columns, categories);
        });
        return columns.sort((p, c) => (p.id < c.id ? -1 : 1));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getColumns(
        columns: MbnaCompliancePopUpColumn[],
        categories: MbnaCompliancePopupCategoriesIdValue,
    ) {
        const ids = Object.keys(categories);
        for (let index = 0; index < ids.length; index++) {
            const category: MbnaCompliancePopupCategory =
                categories[ids[index]];
            // eslint-disable-next-line eqeqeq
            if (columns.some((c) => c.title == category.categoryName)) continue;
            columns.push({
                // eslint-disable-next-line eqeqeq
                isOverall: category.categoryID == 0,
                id: category.categoryID,
                title: category.categoryName,
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateWidget(event: number) {
        this._widgetService.dateRangeCompliance.next(
            this.options.dateRanges[event],
        );
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }

    showDetails(e: Event, item: MbnaCompliancePopupUser): void {
        this.currentDetail = item;
        this.activeTabId = this._tabSet.activeId;
        // eslint-disable-next-line no-return-assign, @typescript-eslint/restrict-plus-operands
        setTimeout(
            () =>
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                (document.querySelector('ngb-modal-window').scrollTop +=
                    this._popupEl.nativeElement.getBoundingClientRect().top),
        );
    }

    enterToUser(e: Event, item: MbnaCompliancePopupUser): void {
        e.stopPropagation();
        console.log(this.drillPath, item);
        if (this.drillPath.filter((i) => i.id === item.id).length > 0) {
            return;
        }
        this.data = null;
        this.currentDetail = null;
        this.hasPendingData = true;
        this.drillPath.push(item);
        this._service
            .mbnaCompliancePopup(
                this.options.definitionId,
                this.populationType,
                item.id,
            )
            .pipe(
                catchError(() => of(null)),
                tap((data) => {
                    this.data = data;
                    this.hasPendingData = false;
                }),
                take(1),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    backDrill(item: MbnaCompliancePopupUser) {
        if (this.drillPath.length < 2) {
            return;
        }
        this.data = null;
        this.currentDetail = null;
        this.hasPendingData = true;
        this.drillPath.splice(this.drillPath.indexOf(item) + 1);
        this._service
            .mbnaCompliancePopup(
                this.options.definitionId,
                this.populationType,
                item.id,
            )
            .pipe(
                catchError(() => of(null)),
                tap((data) => {
                    this.data = data;
                    this.hasPendingData = false;
                }),
                take(1),
            )
            .subscribe();
    }
}
